/**
 * User Settings Store
 *
 * Zustand store for user settings management.
 * Single-responsibility: handles ONLY user settings, not backend options.
 *
 * Terminology:
 * - "Settings" = user-facing umbrella term
 * - "UserSettings" = the type for user-specific settings
 * - "Options" = backend configuration (see backendOptionsStore.ts)
 *
 * This store is platform-agnostic via dependency injection:
 * - Electron: Instantiated with electronSettingsPersistence (IPC-backed)
 * - Web: Instantiated with webSettingsPersistence (API-backed)
 *
 * Note on apiClient usage:
 * This store imports apiClient directly for fetching dropdown options (e.g., model list).
 * This is a pragmatic exception to the DI pattern because:
 * - apiClient is shared infrastructure that works identically across platforms
 * - Model fetching is a simple HTTP GET with no platform-specific behavior
 * - The complexity of injecting a separate IDropdownOptionsProvider isn't justified
 * If this becomes a testing pain point, consider moving to DI in the future.
 *
 * @example
 * ```tsx
 * // Platform entrypoint (e.g., electron/stores/useUserSettingsStore.ts)
 * import { createUserSettingsStore } from '@edgerunner/shared/stores/userSettingsStore';
 * import { electronSettingsPersistence } from '../adapters/electronSettingsPersistence';
 * import { electronSettingsConfigProvider } from '../configs/electronSettingsConfig';
 *
 * export const useUserSettingsStore = createUserSettingsStore(
 *     electronSettingsPersistence,
 *     electronSettingsConfigProvider
 * );
 * ```
 */

import { create, StoreApi, UseBoundStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import { UserSettings, DEFAULT_USER_SETTINGS } from '../types/userSettingsDefinition';
import {
    SettingValue,
    SelectOption,
    isBackendSelect,
    ISettingsPersistence,
    ISettingsConfigProvider,
    UserSettingConfig,
} from '../types/settingsTypes';
import { setNestedValue, getNestedValue } from '../utils/settings/settingsHelpers';
import { apiClient } from '../clients/APIClient';
import { ModelInfo } from '../types/models';

export interface UserSettingsState {
    settings: UserSettings;
    /** Populated at runtime from backend for hybrid select controls (e.g., model list) */
    dropdownOptions: Record<string, SelectOption[]>;
    /** Full ModelInfo cached on initial load — stable for the session lifetime */
    modelInfoCache: Record<string, ModelInfo>;
    isLoading: boolean;
    error?: string;
}

export interface UserSettingsStore extends UserSettingsState {
    actions: {
        load: () => Promise<void>;
        refreshDropdownOptions: () => Promise<void>;
        /** Supports nested keys (e.g., 'export_options.format') */
        update: (key: string, value: SettingValue) => Promise<void>;
        /** For nested keys, resets the top-level parent */
        reset: (key: string) => Promise<void>;
    };
    helpers: {
        getValue: (key: string) => SettingValue;
        getConfig: (key: string) => UserSettingConfig | undefined;
        getAllConfigs: () => Record<string, UserSettingConfig[]>;
        getModelInfo: (modelId: string) => ModelInfo | null;
        isVLM: (modelId: string) => boolean;
    };
}

const enableDevtools = process.env.NODE_ENV !== 'production';

/** Converts nested key updates into the partial object shape persistence expects */
function buildPartialUpdate(
    key: string,
    value: SettingValue,
    current: UserSettings
): Partial<UserSettings> {
    if (key.includes('.')) {
        const updated = JSON.parse(JSON.stringify(current)) as UserSettings;
        setNestedValue(updated, key, value);
        const topKey = key.split('.')[0] as keyof UserSettings;
        return { [topKey]: updated[topKey] } as Partial<UserSettings>;
    }
    return { [key]: value } as Partial<UserSettings>;
}
export const createUserSettingsStore = (
    persistence: ISettingsPersistence,
    configProvider: ISettingsConfigProvider
): UseBoundStore<StoreApi<UserSettingsStore>> =>
    create<UserSettingsStore>()(
        devtools(
            (set, get) => ({
                settings: DEFAULT_USER_SETTINGS,
                dropdownOptions: {},
                modelInfoCache: {},
                isLoading: true,
                error: undefined,

                actions: {
                    load: async () => {
                        set({ isLoading: true, error: undefined }, false, 'load/start');

                        try {
                            const settings = await persistence.loadSettings();
                            set({ settings, isLoading: false }, false, 'load/success');
                        } catch (e) {
                            const error = `Failed to load settings: ${e}`;
                            console.error('userSettingsStore.load:', e);
                            set({ isLoading: false, error }, false, 'load/error');
                        }

						await get().actions.refreshDropdownOptions();
					},

					refreshDropdownOptions: async () => {
						try {
							const configs = configProvider.getSettingsConfig();
							const allConfigs = Object.values(configs).flat();
							const hybridSelects = allConfigs.filter(isBackendSelect);

							const dropdownPromises = hybridSelects.map(async (setting) => {
								const { endpoint } = setting.backendOptionsConfig;
								try {
									const models = await apiClient.models.getModels();
									if (!Array.isArray(models)) {
										throw new Error('Invalid models response');
									}
									const options: SelectOption[] = models.map((m: ModelInfo) => ({
										value: m.id,
										label: m.id,
									}));
									const modelInfoMap: Record<string, ModelInfo> = {};
                                    models.forEach((m: ModelInfo) => { modelInfoMap[m.id] = m; });
									set(
										(state) => ({
											dropdownOptions: {
												...state.dropdownOptions,
												[endpoint]: options,
											},
											modelInfoCache: {
												...state.modelInfoCache,
												...modelInfoMap,
											},
										}),
										false,
										`loadDropdown/${endpoint}`
									);
								} catch (err) {
									console.error(`userSettingsStore: dropdown error for ${endpoint}`, err); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
								}
							});

							await Promise.allSettled(dropdownPromises);
						} catch (e) {
							console.error('userSettingsStore: dropdown init error', e);
						}
					},

                    update: async (key, value) => {
                        set({ isLoading: true }, false, `update/${key}/start`);

                        try {
                            const partial = buildPartialUpdate(key, value, get().settings);
                            const updated = await persistence.writeSettings(partial);
                            set(
                                { settings: updated, isLoading: false, error: undefined },
                                false,
                                `update/${key}/success`
                            );
                        } catch (e) {
                            const error = `Failed to update setting ${key}: ${e}`;
                            console.error(`userSettingsStore.update: ${key}`, e); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                            set({ isLoading: false, error }, false, `update/${key}/error`);
                            throw e;
                        }
                    },

                    reset: async (key) => {
                        set({ isLoading: true }, false, `reset/${key}/start`);

                        try {
                            // Nested keys reset their top-level parent (persistence layer limitation)
                            const topKey = key.split('.')[0] as keyof UserSettings;
                            const updated = await persistence.resetTopLevelSetting(topKey);
                            set(
                                { settings: updated, isLoading: false, error: undefined },
                                false,
                                `reset/${key}/success`
                            );
                        } catch (e) {
                            const error = `Failed to reset setting ${key}: ${e}`;
                            console.error(`userSettingsStore.reset: ${key}`, e); // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
                            set({ isLoading: false, error }, false, `reset/${key}/error`);
                            throw e;
                        }
                    },
                },

                helpers: {
                    getValue: (key) => {
                        const { settings } = get();
                        const value = getNestedValue(settings, key);
                        return (value ?? getNestedValue(DEFAULT_USER_SETTINGS, key)) as SettingValue;
                    },

                    getConfig: (key) => {
                        const { dropdownOptions } = get();
                        const configs = configProvider.getSettingsConfig();
                        const allConfigs = Object.values(configs).flat();
                        const config = allConfigs.find((c) => c.key === key);

                        if (!config) return undefined;

                        // Inject fetched dropdown options into hybrid select configs
                        if (isBackendSelect(config)) {
                            const { endpoint } = config.backendOptionsConfig;
                            const options = dropdownOptions[endpoint];
                            if (options) {
                                return { ...config, options } as UserSettingConfig;
                            }
                        }

                        return config;
                    },

                    getModelInfo: (modelId) => get().modelInfoCache[modelId] ?? null,

                    isVLM: (modelId) => {
                        const info = get().modelInfoCache[modelId];
                        return Boolean(info?.misc?.type?.includes('VLM'));
                    },

                    getAllConfigs: () => {
                        const { dropdownOptions } = get();
                        const configs = configProvider.getSettingsConfig();

                        // Inject fetched dropdown options into all hybrid select configs
                        const populated = Object.fromEntries(
                            Object.entries(configs).map(([category, categorySettings]) => [
                                category,
                                categorySettings.map((setting) => {
                                    if (isBackendSelect(setting)) {
                                        const { endpoint } = setting.backendOptionsConfig;
                                        const options = dropdownOptions[endpoint];
                                        if (options) {
                                            return { ...setting, options } as UserSettingConfig;
                                        }
                                    }
                                    return setting;
                                }),
                            ])
                        ) as Record<string, UserSettingConfig[]>;

                        return populated;
                    },
                },
            }),
            { enabled: enableDevtools, name: 'user-settings-store' }
        )
    );
